import { useState, useEffect } from 'react';
import apiClient from '../../../services/api/client';

interface ClassificationFile {
  [key: string]: string;
}

interface UseClassificationFilesProps {
  clientId?: string;
  workflowId?: string;
  outputFiles?: any;
}

export const useClassificationFiles = ({ clientId, workflowId, outputFiles }: UseClassificationFilesProps) => {
  const [classificationFiles, setClassificationFiles] = useState<ClassificationFile>({});
  const [hasOutputFiles, setHasOutputFiles] = useState(false);
  const [filesWithContent, setFilesWithContent] = useState<{[key: string]: boolean}>({});

  // Process output files when they're loaded
  useEffect(() => {
    if (outputFiles?.files && Array.isArray(outputFiles.files)) {
      console.log('Classification output files:', outputFiles.files);
      
      // Look for relevant.csv, outlier.csv, etc.
      const relevantFiles: ClassificationFile = {};
      let foundOutputFiles = false;
      
      // Check across all files to find classification outputs in any directory
      outputFiles.files.forEach((file: any) => {
        const fileName = file.name.toLowerCase();
        const filePath = file.path || '';
        
        // Check for specific classification output files
        if (fileName === 'relevant.csv' || 
            fileName === 'outlier.csv' || 
            fileName === 'undecided.csv' || 
            fileName === 'stem_words_hierarchy.csv' || 
            fileName === 'topic_framework.json'
          ) {
          
          console.log(`Found classification output file: ${fileName}`);
          relevantFiles[fileName] = filePath;
          foundOutputFiles = true;
        }
      });
      
      if (foundOutputFiles) {
        console.log('Found classification output files:', relevantFiles);
        setClassificationFiles(relevantFiles);
        setHasOutputFiles(true);
        
        // Check content for each file
        Object.values(relevantFiles).forEach(filePath => {
          checkCsvContent(filePath);
        });
      }
    }
  }, [outputFiles]);

  // Scan all directories for classification files
  const scanForClassificationFiles = async () => {
    if (!clientId || !workflowId) return;
    
    console.log('Performing deep scan for classification files');
    try {
      const response = await apiClient.get('/api/v1/storage/list', {
        params: {
          container: 'client-data',
          client_id: clientId,
          workflow_id: workflowId,
          recursive: true
        }
      });
      
      if (response.data?.files?.length) {
        const classificationFileNames = [
          'relevant.csv',
          'outlier.csv', 
          'undecided.csv', 
          'stem_words_hierarchy.csv',
          'topic_framework.json'
        ];
        
        const foundFiles: ClassificationFile = {};
        let foundAny = false;
        
        response.data.files.forEach((file: any) => {
          const fileName = file.name.toLowerCase();
          const filePath = file.path || '';
          
          if (classificationFileNames.includes(fileName)) {
            console.log(`Deep scan found classification file: ${fileName} at ${filePath}`);
            foundFiles[fileName] = filePath;
            foundAny = true;
          }
        });
        
        if (foundAny) {
          console.log('Setting classification files from deep scan:', foundFiles);
          setClassificationFiles(prev => ({...prev, ...foundFiles}));
          setHasOutputFiles(true);
          
          // Check content for display
          Object.values(foundFiles).forEach(filePath => {
            checkCsvContent(filePath);
          });
        }
      }
    } catch (err) {
      console.error('Error during deep scan for classification files:', err);
    }
  };

  // Check if file has content - supports both CSV and JSON files
  const checkCsvContent = async (filePath: string) => {
    if (!filePath || filesWithContent[filePath] !== undefined || !clientId) return;
    
    try {
      console.log(`Checking file content for: ${filePath}`);
      const response = await apiClient.get('/api/v1/storage/get', {
        params: {
          path: filePath,
          container: 'client-data',
          client_id: clientId
        }
      });
      
      const fileContent = response.data;
      
      // Check if content is empty
      if (!fileContent || (typeof fileContent === 'string' && fileContent.trim() === '')) { 
        setFilesWithContent(prev => ({...prev, [filePath]: false}));
        return;
      }
      
      // Check if it's a JSON file
      if (filePath.toLowerCase().endsWith('.json')) {
        try {
          // If it's already parsed as JSON (object), it's valid
          if (typeof fileContent === 'object') {
            setFilesWithContent(prev => ({...prev, [filePath]: true}));
            return;
          }
          
          // Try to parse the string as JSON
          const parsedJson = JSON.parse(fileContent);
          // If it parses without error, mark as having content
          setFilesWithContent(prev => ({...prev, [filePath]: true}));
        } catch (jsonError) {
          console.error(`Error parsing JSON for ${filePath}:`, jsonError);
          setFilesWithContent(prev => ({...prev, [filePath]: false}));
        }
        return;
      }
      
      // For CSV files, continue with existing logic
      const lines = fileContent.split('\n');
      const parsedData: string[][] = [];
      
      // Parse each line
      lines.forEach((line: string) => {
        if (line.trim().length === 0) return;
        
        let row: string[] = [];
        let inQuote = false;
        let currentValue = '';
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          
          if (char === '"' && (i === 0 || line[i-1] !== '\\')) {
            inQuote = !inQuote;
          } else if (char === ',' && !inQuote) {
            row.push(currentValue);
            currentValue = '';
          } else {
            currentValue += char;
          }
        }
        
        if (currentValue) {
          row.push(currentValue);
        }
        
        parsedData.push(row);
      });
      
      // For CSV files, require at least a header and one data row
      setFilesWithContent(prev => ({...prev, [filePath]: parsedData.length > 1}));
    } catch (err) {
      console.error(`Error checking file content for ${filePath}:`, err);
      setFilesWithContent(prev => ({...prev, [filePath]: false}));
    }
  };

  // Add a function to force enable JSON files
  const forceEnableJsonFiles = () => {
    Object.entries(classificationFiles).forEach(([fileName, filePath]) => {
      if (fileName.toLowerCase().endsWith('.json')) {
        console.log(`Force enabling JSON file: ${fileName}`);
        setFilesWithContent(prev => ({...prev, [filePath]: true}));
      }
    });
  };

  // Call force enable when classification files change
  useEffect(() => {
    if (Object.keys(classificationFiles).length > 0) {
      forceEnableJsonFiles();
    }
  }, [classificationFiles]);

  // Run a deep scan after initial load
  useEffect(() => {
    if (workflowId && clientId) {
      const timer = setTimeout(() => {
        if (Object.keys(classificationFiles).length <= 1) {
          console.log('Only found limited classification files, performing deep scan');
          scanForClassificationFiles();
        }
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [workflowId, clientId, classificationFiles]);

  return { 
    classificationFiles, 
    hasOutputFiles, 
    filesWithContent,
    setFilesWithContent,  // Export the state setter function
    scanForClassificationFiles,
    checkCsvContent,
    forceEnableJsonFiles  // Also include the new function we added earlier
  };
};
